/**
 * UI-side companion to `@excellent/core`'s `SECTION_REGISTRY`. Adds the
 * Lucide icon + widget hint per section slug. Lives in @excellent/ui
 * because the canonical registry in core is intentionally data-only (no
 * React, no icons) so that the plain-Node MCP can consume it.
 *
 * Consumers:
 *   • excellent/app/_chrome/section-rail.tsx — renders the rail icon.
 *   • excellent/app/projects/.../page.tsx — picks the widget per section.
 *   • Settings / sections enable list.
 */

import {
  Briefcase,
  Code2,
  DollarSign,
  HeartHandshake,
  Megaphone,
  Palette,
  Scale,
  Target,
  UserPlus,
  type LucideIcon,
} from "../../icons";
// RO-3-02 / RO-4-04: additive subpath, not the bare barrel (see `path-label.ts`
// for the fuller note). `section-registry.ts`'s only import is the equally-pure
// `jtbd-registry.ts`.
import {
  SECTION_REGISTRY,
  type SectionRegistryEntry,
} from "@excellent/core/section-registry";

/**
 * Which auto-detected widget (if any) renders inside the Configure
 * sheet. The actual widget component is imported and constructed by
 * the consuming page — this is just a tag so the page can pick the
 * right one.
 */
export type SectionWidgetKind =
  | "design-audit"
  | "dev-stack"
  | "marketing-analytics"
  | null;

export interface SectionRegistryUiEntry extends SectionRegistryEntry {
  icon: LucideIcon;
  widget: SectionWidgetKind;
}

const ICON_BY_SLUG: Record<string, LucideIcon> = {
  operations: Briefcase,
  design: Palette,
  hiring: UserPlus,
  development: Code2,
  marketing: Megaphone,
  sales: Target,
  "customer-success": HeartHandshake,
  finance: DollarSign,
  legal: Scale,
};

const WIDGET_BY_SLUG: Record<string, SectionWidgetKind> = {
  design: "design-audit",
  development: "dev-stack",
  marketing: "marketing-analytics",
};

export const SECTION_REGISTRY_UI: ReadonlyArray<SectionRegistryUiEntry> =
  SECTION_REGISTRY.map((e) => ({
    ...e,
    icon: ICON_BY_SLUG[e.slug] ?? Briefcase,
    widget: WIDGET_BY_SLUG[e.slug] ?? null,
  }));

const BY_SLUG: Readonly<Record<string, SectionRegistryUiEntry>> =
  Object.fromEntries(SECTION_REGISTRY_UI.map((e) => [e.slug, e]));

export function getSectionRegistryUiEntry(
  slug: string,
): SectionRegistryUiEntry | null {
  return BY_SLUG[slug] ?? null;
}

export function getSectionIcon(slug: string): LucideIcon {
  return ICON_BY_SLUG[slug] ?? Briefcase;
}
